// Module 1 — Onglet Emploi (livraison 2) : la situation professionnelle se lit d'abord ; chaque
// changement passe par une action nommée, datée et tracée (jamais par une édition libre).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Activity, MapPin, UserRound, FileSignature, Phone, Users, ArrowRightLeft, Briefcase, ChevronRight, Pencil, CalendarClock,
} from 'lucide-react';
import { Carte, Champ, Grille, Badge, BadgeStatut, Bouton, BandeauInfo, Avatar, formaterDate } from '../../design/composants';

const NATURE = { REPRISE: 'Reprise de l\'ancienne fiche', INITIAL: 'Contrat initial', RENOUVELLEMENT: 'Renouvellement', AVENANT: 'Avenant', CONVERSION: 'Changement de type' };
const STATUT_VERSION = { EN_VIGUEUR: ['En vigueur', 'succes'], REMPLACE: ['Remplacé', 'neutre'], CLOS: ['Clos', 'neutre'], ANNULE: ['Annulé', 'danger'] };
const nom = (p) => `${p?.prenom || ''} ${(p?.nom || '').toUpperCase()}`.trim();

/** Correction d'une erreur de saisie sur les informations professionnelles (tracée avant / après). */
function ModaleCorrection({ salarieId, s, onFermer, onTermine }) {
  const [f, setF] = useState({ telephone_professionnel: s.telephone_professionnel || '', date_embauche: (s.date_embauche || '').slice(0, 10), motif_correction: '' });
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const enregistrer = async () => {
    setEnCours(true); setErreur('');
    try { await axios.put(`/api/employes/${salarieId}`, f); onTermine('Correction enregistrée et tracée dans l\'historique.'); }
    catch (e) { setErreur(e.response?.data?.message || 'La correction n\'a pas pu être enregistrée.'); }
    finally { setEnCours(false); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label="Corriger les informations professionnelles" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="rh-carte" style={{ width: 480, maxWidth: '100%', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0 }}>
        <h2 className="rh-carte-titre" style={{ marginBottom: 6 }}><Pencil size={17} aria-hidden="true" />Corriger une information</h2>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--sg-texte-3)' }}>Pour une erreur de saisie. Un vrai changement (mutation, contrat…) passe par l'action correspondante.</p>
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {[['Téléphone professionnel', 'telephone_professionnel', 'tel'], ['Date d\'entrée', 'date_embauche', 'date'], ['Motif de la correction', 'motif_correction', 'text']].map(([l, k, t]) => (
          <label key={k} style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>{l}</span>
            <input type={t} className="rh-champ-saisie" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </label>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Bouton onClick={onFermer}>Annuler</Bouton>
          <Bouton variante="primaire" onClick={enregistrer} disabled={enCours}>{enCours ? 'Enregistrement…' : 'Enregistrer la correction'}</Bouton>
        </div>
      </div>
    </div>
  );
}

export default function EmploiSalarie({ salarieId, synthese, subordonnes = [], onAction, onMessage, rafraichir }) {
  const [correction, setCorrection] = useState(false);
  const navigate = useNavigate();
  const [versions, setVersions] = useState(null);
  useEffect(() => {
    let actif = true;
    axios.get(`/api/salaries/${salarieId}/contrats`).then(({ data }) => { if (actif) setVersions(data.contrats); }).catch(() => { if (actif) setVersions([]); });
    return () => { actif = false; };
  }, [salarieId, rafraichir]);

  const { responsable: r, contrat, actions = {}, programmes = [], sortie } = synthese;
  const s = synthese.salarie;
  const rh = actions.rh;
  const enActivite = actions.en_activite;
  const action = (cle, libelle, Icone, props = {}) => rh && <Bouton petit variante="lien" Icone={Icone} onClick={() => onAction(cle)} {...props}>{libelle}</Bouton>;

  return (
    <div>
      {programmes.map((p) => <BandeauInfo key={p.id} ton="alerte" titre="Changement programmé">{p.libelle}. Les effets s'appliqueront automatiquement à cette date.</BandeauInfo>)}

      {/* Refonte visuelle : suspension, réintégration et sortie restent dans le menu « Actions »
          (groupe « Statut ») — actions rares et sensibles, jamais en boutons permanents. */}
      <Carte titre="Situation" Icone={Activity}>
        <Grille>
          <Champ libelle="Statut" enfant={<BadgeStatut statut={s.statut} />} />
          <Champ libelle="Date d'entrée" valeur={formaterDate(s.date_embauche)} />
          <Champ libelle="Ancienneté" valeur={synthese.anciennete?.texte} />
          {sortie && <Champ libelle="Sortie" valeur={`${formaterDate(sortie.date)} — ${sortie.motif}`} />}
          {synthese.acces_application && (
            <Champ libelle="Accès à l'application" enfant={
              synthese.acces_application.autorise ? <Badge ton="succes">Autorisé</Badge>
                : synthese.acces_application.raison === 'COMPTE_BLOQUE' ? <Badge ton="danger">Bloqué (onglet Accès)</Badge>
                  : <Badge ton="neutre">Fermé (salarié sorti)</Badge>} />
          )}
        </Grille>
        {s.statut === 'Suspendu' && synthese.acces_application?.autorise && (
          <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--sg-texte-3)' }}>Salarié suspendu : son accès à l'application reste autorisé. Pour le bloquer, utilisez « Bloquer l'accès » dans l'onglet Accès (décision distincte, motivée et tracée).</p>
        )}
      </Carte>

      <Carte titre="Affectation et métier" Icone={MapPin} action={enActivite && rh && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {action('mutation', 'Changer d\'affectation', ArrowRightLeft)}
        {action('metier', 'Changer de métier', Briefcase)}
      </div>}>
        <Grille>
          <Champ libelle="Site" valeur={s.site ? `${s.site.code ? `${s.site.code} — ` : ''}${s.site.nom}` : null} />
          <Champ libelle="Département" valeur={s.departement} />
          <Champ libelle="Service" valeur={s.service} />
          <Champ libelle="Métier" valeur={synthese.metier?.libelle} />
          <Champ libelle="Poste (organigramme)" valeur={synthese.poste || (synthese.metier ? 'Aucun poste de responsabilité' : null)} />
        </Grille>
        {synthese.libelle_ancien && (
          <p style={{ margin: '14px 0 0', fontSize: 13, color: 'var(--sg-texte-3)' }}>
            Libellé saisi sur l'ancienne fiche : « {synthese.libelle_ancien} ». Il n'est pas encore rattaché à un métier du référentiel.
          </p>
        )}
      </Carte>

      <Carte titre="Responsable hiérarchique" Icone={UserRound} action={enActivite && rh && !s.poste_id && action('responsable', 'Changer de responsable', UserRound)}>
        {r.etat === 'defini' ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Avatar chemin={r.photo_url} prenom={r.prenom} nom={r.nom} taille={40} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <button type="button" className="rh-lien" style={{ fontWeight: 600 }} onClick={() => navigate(`/employes/${r.id}`)}>{nom(r)}</button>
              <div className="rh-cellule-secondaire">{r.intitule || r.poste_responsable}{r.telephone_professionnel ? ` · ${r.telephone_professionnel}` : ''}</div>
            </div>
          </div>
        ) : (
          <Badge ton={r.etat === 'vacant' ? 'alerte' : 'neutre'}>{r.etat === 'vacant' ? 'Poste du responsable vacant' : 'Responsable à définir'}</Badge>
        )}
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--sg-texte-3)' }}>{r.explication}</p>
        {s.poste_id && <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--sg-texte-4)' }}>Pour un titulaire de poste, le responsable se modifie dans l'organigramme (Postes et habilitations).</p>}
      </Carte>

      <Carte titre="Contrat" Icone={FileSignature} action={enActivite && contrat && action('contrat', 'Modifier le contrat', FileSignature)}>
        {contrat ? (
          <Grille>
            <Champ libelle="Type" valeur={contrat.type} />
            <Champ libelle="Depuis le" valeur={formaterDate(contrat.debut)} />
            <Champ libelle="Fin" valeur={contrat.fin ? formaterDate(contrat.fin) : (contrat.type === 'CDI' ? 'Sans date de fin' : null)} />
            <Champ libelle="Origine" valeur={NATURE[contrat.nature]} />
          </Grille>
        ) : <p style={{ margin: 0, fontSize: 13, color: 'var(--sg-texte-3)' }}>{sortie ? 'Contrat clos à la sortie du salarié.' : 'Aucun contrat en vigueur.'}</p>}
        {versions && versions.length > 1 && (
          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--sg-primaire)', fontWeight: 500 }}>Historique du contrat ({versions.length} situations)</summary>
            <ul className="rh-liste" style={{ marginTop: 8 }}>
              {versions.map((v) => {
                const [lib, ton] = STATUT_VERSION[v.statut] || [v.statut, 'neutre'];
                return (
                  <li key={v.id} style={{ flexWrap: 'wrap', opacity: v.statut === 'ANNULE' ? 0.6 : 1 }}>
                    <CalendarClock size={15} color="var(--sg-texte-4)" aria-hidden="true" />
                    <span style={{ flex: 1, minWidth: 220 }}>
                      <strong style={{ fontWeight: 600 }}>{v.type_contrat}</strong> du {formaterDate(v.date_debut)} {v.date_fin ? `au ${formaterDate(v.date_fin)}` : '(sans date de fin)'}
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{NATURE[v.nature]}{v.motif && v.nature !== 'REPRISE' ? ` — ${v.motif}` : ''}{v.statut === 'ANNULE' && v.motif_annulation ? ` — annulé : ${v.motif_annulation}` : ''}</span>
                    </span>
                    <Badge ton={ton}>{lib}</Badge>
                  </li>
                );
              })}
            </ul>
          </details>
        )}
      </Carte>

      {correction && <ModaleCorrection salarieId={salarieId} s={s} onFermer={() => setCorrection(false)} onTermine={(m) => { setCorrection(false); onMessage(m); }} />}
      <Carte titre="Coordonnées professionnelles" Icone={Phone} action={rh && <Bouton petit variante="lien" Icone={Pencil} onClick={() => setCorrection(true)}>Corriger</Bouton>}>
        <Grille>
          <Champ libelle="Téléphone professionnel" valeur={s.telephone_professionnel} />
          <Champ libelle="Matricule" valeur={s.matricule} />
        </Grille>
        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--sg-texte-4)' }}>Le téléphone personnel n'est jamais affiché à la place du professionnel.</p>
      </Carte>

      {subordonnes.length > 0 && (
        <Carte titre={`Équipe directe — ${subordonnes.length} personne${subordonnes.length > 1 ? 's' : ''}`} Icone={Users}>
          <ul className="rh-liste">
            {subordonnes.map((p) => (
              <li key={p.id} className="rh-ligne" tabIndex={0} role="link" onClick={() => navigate(`/employes/${p.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/employes/${p.id}`); }}>
                <Avatar chemin={p.photo_url} prenom={p.prenom} nom={p.nom} taille={32} />
                <span style={{ flex: 1 }}>{nom(p)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{p.poste || p.matricule}</span></span>
                <ChevronRight size={16} color="var(--sg-texte-4)" aria-hidden="true" />
              </li>
            ))}
          </ul>
        </Carte>
      )}
    </div>
  );
}
